import { useMemo } from 'react';
import { itemizedOrderWithAdjustments, safeMoneyFromCents } from '@libs/services/money';
import type { DiscountType } from '@libs/services/money';
import { DiscountTypeEnum, OrderStatusEnum } from '@libs/schemas';
import { trpc } from 'src/trpc';
import type { Cart, Order } from 'src/trpc';

export interface CurrentOrderPricing {
  subtotalInCents: number;
  discountInCents: number;
  /** Names the discount that is actually applied, e.g. `Discount (10% off) - SAVE10`. */
  discountLabel: string;
  totalBeforeTaxInCents: number;
  postTaxAdditionalLineItemsInCents: number;
  taxInCents?: number;
  totalInCents?: number;
  totalPaymentsInCents: number;
  outstandingBalanceInCents?: number;
  isTaxLoading: boolean;
  isTaxError: boolean;
}

const serializeDiscounts = (discounts: DiscountType[]) =>
  discounts.map((discount) => ({
    type: discount.getType(),
    percentage: discount.getType() === DiscountTypeEnum.PERCENTAGE ? discount.getAmount() : 0,
    amountInCents: discount.getType() === DiscountTypeEnum.FLAT ? discount.getAmount() : 0,
    appliedRuleId: '',
  }));

/** The parts of a discount that name it. Shared by persisted, drafted and booked discounts. */
interface NameableDiscount {
  percentage: number;
  amountInCents: number;
  discountCode?: string;
}

/** Whether the cart's booking-time coupon still describes the discount being applied now. */
const bookingCouponStillApplies = (applied: NameableDiscount, booked?: NameableDiscount): boolean =>
  !!booked &&
  (applied.percentage > 0
    ? Math.round(booked.percentage) === Math.round(applied.percentage)
    : booked.percentage <= 0 && booked.amountInCents === applied.amountInCents);

/**
 * Names the discount the order is actually being charged, which is not always the one it was
 * booked with. The cart is an immutable snapshot, so an admin who replaces a booked 10% coupon
 * with a $5 manual discount would otherwise see `10% off` and the old code beside -$5.00, and
 * refetching cannot correct it because the mutation only rewrites the order discounts. The
 * booked percentage and code are shown only while they still describe what is applied, and
 * stacked discounts fall back to a plain label because no single percentage or code names them.
 */
export const describeCurrentDiscount = (
  discounts: NameableDiscount[] | undefined,
  bookedDiscount?: NameableDiscount,
): string => {
  const applied = discounts?.length === 1 ? discounts[0] : undefined;

  if (!applied) {
    return 'Discount';
  }

  const percentage = applied.percentage > 0 ? Math.round(applied.percentage) : 0;
  const code = bookingCouponStillApplies(applied, bookedDiscount)
    ? bookedDiscount?.discountCode
    : undefined;

  return `Discount${percentage > 0 ? ` (${percentage}% off)` : ''}${code ? ` - ${code}` : ''}`;
};

/**
 * Calculates the current order total through the same itemization and tax paths used when the
 * backend charges or refunds an order. The cart is a booking-time snapshot, so raw arithmetic
 * against `lineItems.totalInCents` cannot correctly reapply percentage discounts or tax after
 * an admin adds or removes an add-on.
 */
export const useOrderCurrentPricing = (
  order?: Order | null,
  cart?: Cart | null,
  discountOverride?: DiscountType[],
): CurrentOrderPricing => {
  const itemizedOrder = useMemo(() => {
    if (!order || !cart?.lineItems) {
      return null;
    }

    const finalizedLineItems = order.finalizedLineItems;
    const pricingOrder = discountOverride
      ? { ...order, discounts: serializeDiscounts(discountOverride) }
      : order;

    return itemizedOrderWithAdjustments({
      order: { ...pricingOrder, cart },
      adjustedWeightTotal: safeMoneyFromCents(finalizedLineItems?.weight?.adjustCostInCents || 0),
      adjustedContainerTotal: safeMoneyFromCents(
        finalizedLineItems?.containersAdjustedCostInCents || 0,
      ),
      adjustedMaterialTotal: safeMoneyFromCents(
        finalizedLineItems?.additionalMaterialChargesAdjustedCostInCents || 0,
      ),
    });
  }, [order, cart, discountOverride]);

  // Derived from the same discount set the itemization prices, so the label never describes a
  // coupon the order is no longer being charged.
  const discountLabel = describeCurrentDiscount(
    discountOverride ? serializeDiscounts(discountOverride) : order?.discounts,
    cart?.lineItems?.discount,
  );

  const totalBeforeTaxInCents = itemizedOrder?.totalBeforeTax.getAmount() || 0;
  const canCalculateTax =
    !!order?._id &&
    !!order.userId &&
    !!cart?.lineItems &&
    order.status !== OrderStatusEnum.Restricted;
  const taxQuery = trpc.admin.order.adminCalculateTaxes.useQuery(
    {
      orderId: order?._id?.toString() || '',
      userId: order?.userId || '',
      subTotal: totalBeforeTaxInCents,
    },
    {
      enabled: canCalculateTax,
      staleTime: 30000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  return useMemo(() => {
    const subtotalInCents = itemizedOrder?.subtotal.getAmount() || 0;
    const discountInCents = itemizedOrder?.discount.getAmount() || 0;
    const postTaxAdditionalLineItemsInCents =
      itemizedOrder?.postTaxAdditionalLineItemsTotal.getAmount() || 0;
    const totalPaymentsInCents =
      order?.payments?.reduce(
        (total, payment) => total + payment.amountPaid - (payment.amountRefunded || 0),
        0,
      ) || 0;
    const hasTaxResult = canCalculateTax && taxQuery.data !== undefined && !taxQuery.isError;
    const taxInCents = hasTaxResult ? taxQuery.data : undefined;
    const totalInCents =
      taxInCents === undefined
        ? undefined
        : totalBeforeTaxInCents + taxInCents + postTaxAdditionalLineItemsInCents;

    return {
      subtotalInCents,
      discountInCents,
      discountLabel,
      totalBeforeTaxInCents,
      postTaxAdditionalLineItemsInCents,
      taxInCents,
      totalInCents,
      totalPaymentsInCents,
      outstandingBalanceInCents:
        totalInCents === undefined ? undefined : totalInCents - totalPaymentsInCents,
      isTaxLoading: canCalculateTax && taxQuery.isLoading,
      isTaxError: canCalculateTax && taxQuery.isError,
    };
  }, [
    itemizedOrder,
    discountLabel,
    order?.payments,
    canCalculateTax,
    taxQuery.data,
    taxQuery.isLoading,
    taxQuery.isError,
    totalBeforeTaxInCents,
  ]);
};
